import {
  ApolloClient,
  ApolloLink,
  type FieldFunctionOptions,
  HttpLink,
  InMemoryCache,
  ApolloProvider as Provider,
  type Reference,
  type StoreObject,
  from,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { removeTypenameFromVariables } from "@apollo/client/link/remove-typename";
import { RetryLink } from "@apollo/client/link/retry";
import { signOut as signOutAuth } from "aws-amplify/auth";
import { useTenantId } from "hooks/useTenant";
import { PropsWithChildren, useEffect, useLayoutEffect, useMemo } from "react";

import { resetPosthog } from "../instrumentation/posthog";
import { bolt } from "../utils/metricsLogger";
import { useToken } from "./token";

const removeTypenameLink = removeTypenameFromVariables();
const SCOUT_DASHBOARD_METRICS_OPERATION_NAME = "ScoutDashboardMetrics";

// Logging link for performance diagnostics - logs all GraphQL requests to bolt
const boltLink = new ApolloLink((operation, forward) => {
  const startTime = performance.now();
  const operationName = operation.operationName;

  if (operationName === SCOUT_DASHBOARD_METRICS_OPERATION_NAME) {
    // This dashboard polls large metric payloads continuously. In local dev, the
    // metrics logger keeps query events in memory, so logging every poll can
    // look like an Apollo cache leak during long dashboard sessions.
    return forward(operation);
  }

  const variables = operation.variables;
  const context = operation.getContext();
  const fetchPolicy = (context.fetchPolicy as string) ?? "cache-first";

  // Log query start
  bolt.logQueryStart({
    queryName: operationName,
    variables,
    fetchPolicy,
  });

  return forward(operation).map((response) => {
    const durationMs = performance.now() - startTime;

    // Estimate response size for diagnostics
    let responseSize = 0;
    try {
      responseSize = JSON.stringify(response).length;
    } catch {
      // Ignore serialization errors
    }

    // Log query resolve
    bolt.logQueryResolve({
      queryName: operationName,
      durationMs,
      variables,
      responseSize,
    });

    return response;
  });
});

const httpLink = new HttpLink({
  uri: import.meta.env.VITE_API_URL
    ? `${import.meta.env.VITE_API_URL}/graphql`
    : "http://localhost:4000/graphql",
});

/**
 * Retries failed network calls so the app recovers automatically when the API
 * comes back (dev server still booting after a laptop sleep, brief service
 * blips, etc.). We retry on raw network errors only — GraphQL errors with a
 * status code are real semantic failures and should surface to the caller as
 * normal. Auth errors (401) short-circuit here because they're already handled
 * by `createErrorLink` which signs the user out, so retrying them would just
 * spam the API while a redirect is in flight.
 */
const retryLink = new RetryLink({
  delay: {
    initial: 500,
    max: 8000,
    jitter: true,
  },
  attempts: {
    max: 5,
    retryIf: (error, _operation) => {
      if (!error) return false;
      const status =
        typeof error === "object" && error !== null && "statusCode" in error
          ? (error as { statusCode?: number }).statusCode
          : undefined;
      // Don't retry auth failures or other definitive HTTP responses.
      if (typeof status === "number") {
        return false;
      }
      // No status code means we never reached the server (TypeError: Failed to
      // fetch, ERR_CONNECTION_REFUSED, etc.) — safe to retry.
      return true;
    },
  },
});

type PagedCacheItem = Reference | StoreObject;

type PagedCacheValue = {
  __typename?: string;
  count?: number;
  items?: readonly PagedCacheItem[];
};

/**
 * De-dupes merged rows by `id` after Apollo combines paginated `fetchMore`
 * results into a single cached list.
 *
 * Apollo's pagination docs recommend a field-policy `merge` function so
 * follow-up pages can be combined in cache instead of stored as separate
 * results:
 * https://www.apollographql.com/docs/react/pagination/core-api
 *
 * We additionally de-dupe by `id` because page-based requests can overlap
 * during refetches or when rows move between offsets, and no surface should
 * render the same row twice.
 */
function dedupePagedItems(
  items: readonly PagedCacheItem[],
  { readField }: Pick<FieldFunctionOptions, "readField">
): PagedCacheItem[] {
  const seenIds = new Set<string>();
  const dedupedItems: PagedCacheItem[] = [];

  for (const item of items) {
    const id = readField<string>("id", item);

    if (id == null) {
      dedupedItems.push(item);
      continue;
    }

    if (seenIds.has(id)) {
      continue;
    }

    seenIds.add(id);
    dedupedItems.push(item);
  }

  return dedupedItems;
}

/**
 * Field-policy merge for any `{ count, items }` field paged by `page`/`pageSize`.
 *
 * Apollo's offset-pagination guidance uses a `merge` function that places
 * incoming rows at the correct offset instead of blindly appending them:
 * https://www.apollographql.com/docs/react/pagination/core-api
 * https://www.apollographql.com/docs/react/pagination/offset-based
 *
 * We cannot use `offsetLimitPagination()` directly because these fields return
 * an object rather than a bare array, so we merge the `items` array manually
 * while still preserving the latest `count` for the caller's `hasMore` check.
 * Each field's `keyArgs` are configured separately so different filters do not
 * share the same cached list:
 * https://www.apollographql.com/docs/react/pagination/key-args
 */
function mergePagedField(
  existing: PagedCacheValue | undefined,
  incoming: PagedCacheValue,
  options: FieldFunctionOptions
): PagedCacheValue {
  const page = typeof options.args?.page === "number" ? options.args.page : 0;
  const incomingItems = incoming.items ?? [];

  if (page === 0) {
    return {
      ...incoming,
      items: dedupePagedItems(incomingItems, options),
    };
  }

  const pageSize =
    typeof options.args?.pageSize === "number" ? options.args.pageSize : incomingItems.length;
  const startIndex = page * pageSize;
  const mergedItems = existing?.items ? [...existing.items] : [];

  for (const [index, item] of incomingItems.entries()) {
    mergedItems[startIndex + index] = item;
  }

  return {
    ...existing,
    ...incoming,
    items: dedupePagedItems(mergedItems, options),
  };
}

const createErrorLink = () =>
  onError(({ graphQLErrors, networkError }) => {
    // We don't want to redirect to the login page if we are already on the login page
    // that could cause an infinite redirect
    // since / hits the API
    const isOnLoginPage = window.location.pathname === "/";

    const handleAuthError = () => {
      if (!isOnLoginPage) {
        // Mark that we're logging out to prevent redirect loops
        localStorage.setItem("logout", "true");

        // Clear PostHog identity + session keys so a forced logout on a shared
        // browser doesn't merge the next user into this profile.
        resetPosthog();

        // Sign out from AWS Amplify to clear all authentication state
        signOutAuth()
          .then(() => {
            // Force a full page reload to clear any cached state
            window.location.href = "/";
          })
          .catch((signOutError) => {
            console.error("Error during signout:", signOutError);
            // Even if signout fails, still redirect to clear the app state
            window.location.href = "/";
          });
      }
    };

    if (graphQLErrors) {
      graphQLErrors.forEach(({ extensions }) => {
        if (extensions?.code === "UNAUTHENTICATED") {
          handleAuthError();
        }
      });
    }

    if (networkError && "statusCode" in networkError && networkError.statusCode === 401) {
      handleAuthError();
    }
  });

export const client = new ApolloClient({
  cache: new InMemoryCache({
    typePolicies: {
      ScoutCostUsageSummary: {
        // Usage snapshots are per-row context; normalizing identical values across
        // tenants made Apollo collapse list items when reading the grid query.
        keyFields: false,
      },
      WasteContainer: {
        fields: {
          // Computed, id-less object fetched with different field sets by the
          // list (deadline/status only) and detail (full limits) queries;
          // shallow-merge so one selection can't clobber the other's cache.
          clock: {
            merge: true,
          },
        },
      },
      HdHandler: {
        keyFields: ["handlerId", "activityLocation", "sourceType", "seqNumber"],
      },
      Document: {
        fields: {
          // `facilityDocument` is the per-(document, facility) join exposed on the
          // normalized `Document:<id>` entry — which is shared across facilities.
          // Key the cached value by the requesting query's facility/report context
          // so viewing the same doc under facility B can't overwrite the value
          // cached for facility A (and a null from a multi/zero-facility search
          // can't clobber a facility-scoped one). Each context gets its own slot
          // instead of a single shared pointer (ENG-15528).
          facilityDocument: {
            keyArgs: (_args, { variables }) =>
              `facilityDocument:${
                variables?.facilityId ?? variables?.reportId ?? variables?.search ?? "_"
              }`,
            merge: (existing, incoming) => incoming ?? existing,
          },
        },
      },
      Payment: {
        fields: {
          facilities: {
            merge(_, incoming) {
              return incoming;
            },
          },
        },
      },
      TierIIReport: {
        fields: {
          assignees: {
            merge(_, incoming) {
              return incoming;
            },
          },
          reviewers: {
            merge(_, incoming) {
              return incoming;
            },
          },
        },
      },
      Submission: {
        fields: {
          facilitySubmissions: {
            merge(_, incoming) {
              return incoming;
            },
          },
        },
      },
      Tenant: {
        fields: {
          /**
           * The compliance dashboard's two feeds, both paged for "load more".
           * Keyed by their filters only, so successive pages of one filter extend
           * a single cached list while a different filter gets its own — and so
           * the per-record badge hook, which asks for its own `kinds`, cannot
           * share a list with the dashboard.
           */
          containerComplianceFindings: {
            keyArgs: ["kinds", "facilityId"],
            merge: mergePagedField,
          },
          manifestComplianceFindings: {
            keyArgs: ["kinds", "facilityId"],
            merge: mergePagedField,
          },
        },
      },
      // Merge policy for wasteSubmissionsSummary to prevent cache warnings
      Query: {
        fields: {
          getScoutConversations: {
            /**
             * Key the cache entry by the stable filter inputs only so Apollo
             * stores every page for the same search in one list that `merge`
             * can extend for infinite scroll. Including `page` or `pageSize`
             * here would split each fetchMore response into a separate entry.
             */
            keyArgs: ["tenantId", "search", "sort"],
            merge(existing, incoming, options) {
              return mergePagedField(existing, incoming, options);
            },
          },
          wasteSubmissionsSummary: {
            merge(_, incoming) {
              return incoming;
            },
          },
          getScoutTenantCostSummary: {
            keyArgs: ["search", "sort"],
            merge(_, incoming) {
              return incoming;
            },
          },
        },
      },
    },
  }),
  // Provide initial link to satisfy Apollo Client 4.0 requirement
  // This will be replaced by setLink() in ApolloProvider once auth is available
  link: from([boltLink, removeTypenameLink, createErrorLink(), retryLink, httpLink]),
  devtools: {
    enabled: import.meta.env.VITE_ENV !== "release",
  },
});

/**
 * Resolves which tenant ID to send as the x-encamp-tenant-id header.
 *
 * Priority:
 *  1. Explicit override from Apollo context (used by staff-facing pickers
 *     that need to query a specific customer tenant)
 *  2. Empty string for staff routes (allows cross-tenant navigation)
 *  3. The current tenant from useTenantId()
 */
export function resolveTenantIdHeader(
  tenantIdOverride: string | undefined,
  pathname: string,
  currentTenantId: string | undefined
): string {
  if (tenantIdOverride != null) return tenantIdOverride;
  if (pathname.startsWith("/staff")) return "";
  return currentTenantId ?? "";
}

export function ApolloProvider(props: PropsWithChildren<object>) {
  const { getIdTokenRefreshIfExpired, isAuthenticated } = useToken();

  const currentTenantId = useTenantId();

  const tenantLink = useMemo(
    () =>
      setContext((_, previousContext) => {
        // See resolveTenantIdHeader for the full priority description.
        // Callers can pass `{ tenantIdOverride: "<id>" }` as Apollo context
        // to target a specific tenant (used by staff-facing pickers).
        const { headers, tenantIdOverride } = previousContext;
        return {
          headers: {
            ...headers,
            "x-encamp-tenant-id": resolveTenantIdHeader(
              tenantIdOverride,
              window.location.pathname,
              currentTenantId
            ),
          },
        };
      }),
    [currentTenantId]
  );

  // useLayoutEffect (as opposed to useEffect) forces this to be synchronous so it will finish
  // running before the component is rendered. That is necessary because we need to make the
  // client.setLink call before any child components attempt to actually use the client
  useLayoutEffect(() => {
    const authLink = setContext(async (_, { headers }) => {
      const idToken = await getIdTokenRefreshIfExpired();
      return {
        headers: {
          ...headers,
          "Content-Type": "application/json",
          authorization: idToken ? `Bearer ${idToken}` : "",
        },
      };
    });

    client.setLink(
      from([
        boltLink,
        removeTypenameLink,
        createErrorLink(),
        authLink,
        tenantLink,
        retryLink,
        httpLink,
      ])
    );
  }, [getIdTokenRefreshIfExpired, tenantLink]);

  useEffect(() => {
    if (!isAuthenticated) {
      client.clearStore();
    }
  }, [isAuthenticated]);

  return <Provider client={client}>{props.children}</Provider>;
}
